import { useMemo } from 'react';

import { DateTime } from 'luxon';

import { Message, isMessageRead } from '@/models/MessagingModel.ts';
import { PatientEvent } from '@/models/PatientEventModel';
import { Queries } from '@/queries/Queries.ts';
import { DateUtils, toDateTime } from '@/utils/dateUtils.ts';

export type ChatMessageContent = {
  type: 'message';
  sentDate: DateTime;
  message: Message;
};

export type ChatNewDayContent = {
  type: 'newDay';
  date: DateTime;
};

export type ChatUnreadContent = {
  type: 'unread';
};

export type BaseEvent = {
  type: 'event';
  date: DateTime;
  event: PatientEvent;
};

export type ChatContent =
  | ChatMessageContent
  | ChatNewDayContent
  | ChatUnreadContent
  | BaseEvent;

type ReducerData = {
  content: ChatContent[];
  currentDay: DateTime | undefined;
  isUnread: boolean;
};

type ChatContentReturn = {
  content: ChatContent[];
  loadMore: () => void;
};

export const useChatContent = (
  patientId: string,
  practitionerId: string,
): ChatContentReturn => {
  const messages = Queries.messaging.useMessages(
    { patientId },
    {
      select: data => data.pages.flatMap(page => page.results).reverse(),
    },
  );

  const events = Queries.practitioner.usePatientEvents(patientId);

  const data = useMemo(() => {
    const normalizedMessages = (messages.data ?? []).map(msg => ({
      kind: 'message' as const,
      createdAt: msg.createdAt,
      payload: msg,
    }));

    const normalizedEvents = events.map(ev => ({
      kind: 'event' as const,
      createdAt: ev.created_at,
      payload: ev,
    }));

    const merged = [...normalizedMessages, ...normalizedEvents].sort(
      (a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    );

    const reducedData = merged.reduce<ReducerData>(
      (acc, item) => {
        const itemDate = toDateTime(item.createdAt).startOf('day');

        if (
          !acc.currentDay ||
          !DateUtils.dateEquals(acc.currentDay, itemDate)
        ) {
          acc.content.push({ type: 'newDay', date: itemDate });
        }

        if (item.kind === 'message') {
          const message = item.payload;

          if (!acc.isUnread && !isMessageRead(message, practitionerId)) {
            acc.content.push({ type: 'unread' });
            acc.isUnread = true;
          }

          acc.content.push({
            type: 'message',
            sentDate: toDateTime(message.createdAt),
            message,
          });
        } else {
          const event = item.payload;
          acc.content.push({
            type: 'event',
            date: toDateTime(event.created_at),
            event,
          });
        }

        acc.currentDay = itemDate;
        return acc;
      },
      { content: [], currentDay: undefined, isUnread: false },
    );

    return reducedData;
  }, [messages.data, events, practitionerId]);

  return useMemo(
    () => ({
      content: data.content,
      loadMore: () => messages.fetchNextPage(),
    }),
    [data.content, messages],
  );
};
